/**
 * T020 — 任务分派看板（模块十三 4.11，场景 13.1–13.9）
 *
 * 功能：
 *  13.1 单任务分派（格子 + 号选任务）
 *  13.2 Bundle 批量（同格多任务勾选）
 *  13.3 笛卡尔积（多成员×多日期×多任务）
 *  13.4 排班冲突提示（黄=需确认，蓝=不阻塞，加班=不提示）
 *  13.5 成员改期（仅改 start_date；原日期幽灵芯片）
 *  13.6 原计划 vs 实际可视化（幽灵芯片）
 *  13.7 撤回（todo+elapsed=0 直删，进行中二次确认，done 不可）
 *  13.8 改派（拖到新成员+日期，progress 暂停旧计时器）
 *  13.9 权限（Manager 受 scope，Admin 全局；成员只读，自创只读）
 */

import { useCallback, useEffect, useMemo, useRef, useState } from 'react'
import { useAuth } from '../auth'
import { useLang } from '../i18n'

// ── Types ────────────────────────────────────────────────────────────────────

interface MemberInfo {
  id: string
  display_name: string
  group_name: string
  region_code: string
  role: string
  is_active: boolean
  account_status: string
}

interface TaskDictItem {
  id: string
  name: string
  category_code: string
  is_active: boolean
  deleted_at: string | null
}

interface AssignedTask {
  id: string
  assignee_id: string
  assignee_name: string | null
  category_code: string | null
  task_id: string | null
  title: string
  start_date: string
  original_start_date: string
  complete_date: string | null
  status: 'todo' | 'progress' | 'done'
  volume: number | null
  elapsed_seconds: number
  timer_state: 'idle' | 'running' | 'paused'
  assigned_by: string | null
}

interface ConflictResult {
  member_id: string
  date: string
  raw_status: string | null
  level: 'none' | 'blue' | 'yellow'
}

interface WithdrawNeeds {
  taskId: string
  task: AssignedTask
  elapsed_seconds: number
}

// ── Helpers ──────────────────────────────────────────────────────────────────

function authHeader(token: string | null): Record<string, string> {
  return token ? { Authorization: `Bearer ${token}` } : {}
}

function todayStr(): string {
  return new Date().toISOString().slice(0, 10)
}

function addDays(dateStr: string, n: number): string {
  const d = new Date(dateStr + 'T00:00:00')
  d.setDate(d.getDate() + n)
  return d.toISOString().slice(0, 10)
}

function formatDate(dateStr: string): string {
  const d = new Date(dateStr + 'T00:00:00')
  return `${d.getMonth() + 1}/${d.getDate()}`
}

function weekdayIndex(dateStr: string): number {
  return new Date(dateStr + 'T00:00:00').getDay() // 0=Sun, 6=Sat
}

function statusTokens(status: AssignedTask['status']): { border: string; bg: string } {
  switch (status) {
    case 'done': return { border: 'var(--success)', bg: 'var(--success-bg)' }
    case 'progress': return { border: 'var(--info)', bg: 'var(--info-bg)' }
    default: return { border: 'var(--border)', bg: 'var(--bg-elevated)' }
  }
}

function statusLabel(status: AssignedTask['status'], t: (k: string) => string): string {
  switch (status) {
    case 'done': return t('assignStatusDone')
    case 'progress': return t('assignStatusProgress')
    default: return t('assignStatusTodo')
  }
}

function minutesToDisplay(min: number): string {
  const h = Math.floor(min / 60)
  const m = min % 60
  if (h > 0) return `${h}h${m > 0 ? m + 'm' : ''}`
  return m > 0 ? `${m}m` : '0m'
}

function buildDateRange(start: string, days: number): string[] {
  const range: string[] = []
  for (let i = 0; i < days; i++) range.push(addDays(start, i))
  return range
}

const labelStyle: React.CSSProperties = {
  display: 'flex',
  flexDirection: 'column',
  fontSize: 12,
  marginBottom: 10,
  gap: 3,
}

const inputStyle: React.CSSProperties = {
  fontSize: 13,
  padding: '4px 6px',
  border: '1px solid var(--border)',
  borderRadius: 4,
  background: 'var(--bg-surface)',
  color: 'var(--text-primary)',
}

// ── Main Component ───────────────────────────────────────────────────────────

export function AssignmentsPage() {
  const { t } = useLang()
  const { user, accessToken } = useAuth()

  const isManagerOrAdmin = user?.role === 'manager' || user?.role === 'admin'
  const isMember = user?.role === 'member'

  // Board date range — default: today, 14 days (req 9: reset on each mount)
  const [rangeStart, setRangeStart] = useState<string>(todayStr)
  const [rangeDays, setRangeDays] = useState(14)

  // Req 9: reset start date to today every time the board opens
  useEffect(() => {
    setRangeStart(todayStr())
  }, [])

  const dates = useMemo(() => buildDateRange(rangeStart, rangeDays), [rangeStart, rangeDays])

  // Data
  const [members, setMembers] = useState<MemberInfo[]>([])
  const [taskDict, setTaskDict] = useState<TaskDictItem[]>([])
  const [tasks, setTasks] = useState<AssignedTask[]>([])
  const [conflictMap, setConflictMap] = useState<Record<string, ConflictResult>>({})
  const [loading, setLoading] = useState(false)
  const [error, setError] = useState<string | null>(null)
  const [toast, setToast] = useState<string | null>(null)

  // Modal states
  const [assignModal, setAssignModal] = useState<{
    mode: 'single' | 'bundle' | 'cartesian'
    prefillMemberId?: string
    prefillDate?: string
  } | null>(null)

  const [withdrawNeeds, setWithdrawNeeds] = useState<WithdrawNeeds | null>(null)
  const [reassignTarget, setReassignTarget] = useState<{ taskId: string; task: AssignedTask } | null>(null)
  const [rescheduleTarget, setRescheduleTarget] = useState<{ taskId: string; task: AssignedTask } | null>(null)
  const [filterMemberId, setFilterMemberId] = useState<string>('')

  const showToast = useCallback((msg: string) => {
    setToast(msg)
    setTimeout(() => setToast(null), 3000)
  }, [])

  // ── Data loading ──────────────────────────────────────────────────────────

  const loadAll = useCallback(async () => {
    if (!accessToken) return
    setLoading(true)
    setError(null)
    const headers = authHeader(accessToken)
    const dateEnd = addDays(rangeStart, rangeDays - 1)

    // Phase 1: network fetch — TypeError = network failure; other errors = JS bug
    let membersRes!: Response, tasksRes!: Response, dictRes!: Response
    try {
      const rs = await Promise.all([
        fetch('/api/members', { headers }),
        fetch(`/api/assignments?date_from=${rangeStart}&date_to=${dateEnd}`, { headers }),
        fetch('/api/tasks', { headers }),
      ])
      membersRes = rs[0]; tasksRes = rs[1]; dictRes = rs[2]
    } catch (err) {
      setError(err instanceof TypeError ? '网络错误，请重试' : '加载出错，请重试')
      setLoading(false)
      return
    }

    // Phase 2: parse & apply — JS bugs here show '加载出错', not '网络错误'
    try {
      if (!membersRes.ok || !tasksRes.ok || !dictRes.ok) {
        const failedRes = [membersRes, tasksRes, dictRes].find(r => !r.ok)!
        try {
          const errData = await failedRes.json()
          setError(errData.error?.message || '加载失败')
        } catch {
          setError('加载失败')
        }
        return
      }

      const membersData = await membersRes.json()
      const tasksData = await tasksRes.json()
      const dictData = await dictRes.json()

      const activeMembers: MemberInfo[] = (membersData.members || []).filter(
        (m: MemberInfo) => m.role !== 'admin' && m.account_status !== 'deactivated'
      )
      setMembers(activeMembers)
      setTasks(tasksData.data || [])
      // Bug-a fix: /api/tasks returns {data:[...]} (not .tasks); backend already filters is_active=true
      setTaskDict(dictData.data || [])

      // Conflict check for manager/admin — best-effort, failures are non-fatal
      if (isManagerOrAdmin && activeMembers.length > 0) {
        const pairs = activeMembers.flatMap(m =>
          dates.map(d => ({ member_id: m.id, date: d }))
        ).slice(0, 300)

        if (pairs.length > 0) {
          try {
            const conflictRes = await fetch(
              `/api/assignments/conflict-check?pairs=${encodeURIComponent(JSON.stringify(pairs))}`,
              { headers }
            )
            if (conflictRes.ok) {
              const conflictData = await conflictRes.json()
              const map: Record<string, ConflictResult> = {}
              for (const item of (conflictData.data || [])) {
                map[`${item.member_id}_${item.date}`] = item
              }
              setConflictMap(map)
            }
          } catch {
            // Non-critical — conflict indicators are best-effort
          }
        }
      }
    } catch {
      setError('加载出错，请重试')
    } finally {
      setLoading(false)
    }
  }, [accessToken, rangeStart, rangeDays, dates, isManagerOrAdmin])

  useEffect(() => { loadAll() }, [loadAll])

  // ── Board data ────────────────────────────────────────────────────────────

  const visibleMembers = useMemo(() => {
    if (isMember) return members.filter(m => m.id === user?.id)
    if (filterMemberId) return members.filter(m => m.id === filterMemberId)
    return members
  }, [members, isMember, filterMemberId, user?.id])

  const tasksByCell = useMemo(() => {
    const map: Record<string, AssignedTask[]> = {}
    for (const task of tasks) {
      const key = `${task.assignee_id}_${task.start_date}`
      if (!map[key]) map[key] = []
      map[key].push(task)
    }
    return map
  }, [tasks])

  // Ghost: original_start_date cell for rescheduled tasks
  const ghostByCell = useMemo(() => {
    const map: Record<string, AssignedTask[]> = {}
    for (const task of tasks) {
      if (task.start_date !== task.original_start_date) {
        const key = `${task.assignee_id}_${task.original_start_date}`
        if (!map[key]) map[key] = []
        map[key].push(task)
      }
    }
    return map
  }, [tasks])

  // ── Actions ───────────────────────────────────────────────────────────────

  const doWithdraw = useCallback(async (taskId: string, confirmed: boolean) => {
    if (!accessToken) return
    try {
      const res = await fetch(`/api/assignments/${taskId}/withdraw`, {
        method: 'POST',
        headers: { ...authHeader(accessToken), 'Content-Type': 'application/json' },
        body: JSON.stringify({ confirmed }),
      })
      const data = await res.json()
      if (!res.ok) {
        showToast(data.error?.message || '撤回失败')
        return
      }
      if (data.needs_confirm) {
        const task = tasks.find(task_ => task_.id === taskId)
        if (task) setWithdrawNeeds({ taskId, task, elapsed_seconds: data.elapsed_seconds })
        return
      }
      showToast('已撤回')
      setWithdrawNeeds(null)
      loadAll()
    } catch {
      showToast('网络错误，请重试')
    }
  }, [accessToken, tasks, loadAll, showToast])

  const doReassign = useCallback(async (taskId: string, newAssigneeId: string, newDate: string) => {
    if (!accessToken) return
    try {
      const res = await fetch(`/api/assignments/${taskId}/reassign`, {
        method: 'POST',
        headers: { ...authHeader(accessToken), 'Content-Type': 'application/json' },
        body: JSON.stringify({ new_assignee_id: newAssigneeId, new_start_date: newDate }),
      })
      const data = await res.json()
      if (!res.ok) {
        showToast(data.error?.message || '改派失败')
        return
      }
      showToast('已改派')
      setReassignTarget(null)
      loadAll()
    } catch {
      showToast('网络错误，请重试')
    }
  }, [accessToken, loadAll, showToast])

  const doReschedule = useCallback(async (taskId: string, newDate: string) => {
    if (!accessToken) return
    try {
      const res = await fetch(`/api/assignments/${taskId}/reschedule`, {
        method: 'PATCH',
        headers: { ...authHeader(accessToken), 'Content-Type': 'application/json' },
        body: JSON.stringify({ new_start_date: newDate }),
      })
      const data = await res.json()
      if (!res.ok) {
        showToast(data.error?.message || '改期失败')
        return
      }
      showToast('已改期')
      setRescheduleTarget(null)
      loadAll()
    } catch {
      showToast('网络错误，请重试')
    }
  }, [accessToken, loadAll, showToast])

  // ── Drag & drop ───────────────────────────────────────────────────────────

  const dragTaskRef = useRef<AssignedTask | null>(null)

  function handleDragStart(task: AssignedTask) {
    dragTaskRef.current = task
  }

  function handleDrop(memberId: string, date: string) {
    const task = dragTaskRef.current
    dragTaskRef.current = null
    if (!task) return
    if (task.status === 'done') { showToast('已完成任务不可拖动'); return }

    if (isMember) {
      if (task.assignee_id === user?.id && task.assigned_by) {
        doReschedule(task.id, date)
      }
      return
    }

    if (memberId === task.assignee_id && date === task.start_date) return

    const conflictKey = `${memberId}_${date}`
    const conflict = conflictMap[conflictKey]
    if (conflict?.level === 'yellow') {
      if (!window.confirm(t('assignConflictYellow'))) return
    }
    doReassign(task.id, memberId, date)
  }

  // ── Render ────────────────────────────────────────────────────────────────

  const cellStyle: React.CSSProperties = {
    minWidth: 96,
    minHeight: 54,
    border: '1px solid var(--border)',
    verticalAlign: 'top',
    padding: '2px 4px',
    position: 'relative',
  }

  const headerCellStyle: React.CSSProperties = {
    minWidth: 96,
    padding: '4px 6px',
    border: '1px solid var(--border)',
    background: 'var(--bg-page)',
    fontWeight: 600,
    textAlign: 'center',
    fontSize: 11,
    color: 'var(--text-secondary)',
    // T001: sticky date header row (vertical scroll)
    position: 'sticky',
    top: 0,
    zIndex: 2,
  }

  const memberCellStyle: React.CSSProperties = {
    minWidth: 110,
    padding: '4px 8px',
    border: '1px solid var(--border)',
    background: 'var(--bg-elevated)',
    fontWeight: 500,
    fontSize: 12,
    whiteSpace: 'nowrap',
    color: 'var(--text-primary)',
    // T001: sticky member column (horizontal scroll)
    position: 'sticky',
    left: 0,
    zIndex: 1,
  }

  function renderConflictDot(memberId: string, date: string) {
    const c = conflictMap[`${memberId}_${date}`]
    if (!c || c.level === 'none') return null
    const color = c.level === 'yellow' ? 'var(--warning)' : 'var(--info)'
    return (
      <div
        title={c.level === 'yellow' ? t('assignConflictYellow') : t('assignConflictBlue')}
        style={{
          position: 'absolute', top: 2, right: 2,
          width: 7, height: 7, borderRadius: '50%',
          background: color, cursor: 'help',
        }}
      />
    )
  }

  function renderChip(task: AssignedTask, isGhost: boolean) {
    const canManage = isManagerOrAdmin && !isGhost && !!task.assigned_by
    const canReschedule = isMember && task.assignee_id === user?.id && !!task.assigned_by

    const tokens = isGhost ? null : statusTokens(task.status)
    const chipStyle: React.CSSProperties = {
      display: 'flex',
      alignItems: 'center',
      gap: 3,
      padding: '1px 5px',
      borderRadius: 3,
      marginBottom: 2,
      fontSize: 11,
      cursor: isGhost ? 'default' : (canManage || canReschedule ? 'grab' : 'default'),
      border: isGhost ? '1px dashed var(--border-strong)' : `1px solid ${tokens!.border}`,
      background: isGhost ? 'transparent' : tokens!.bg,
      color: isGhost ? 'var(--text-muted)' : 'var(--text-primary)',
      opacity: isGhost ? 0.7 : 1,
      maxWidth: 130,
      overflow: 'hidden',
    }

    const label = isGhost
      ? `👻 ${task.title} ⟶ ${formatDate(task.start_date)}`
      : `${task.title}${task.start_date !== task.original_start_date ? ' ↩️' : ''}`

    return (
      <div
        key={isGhost ? `ghost_${task.id}` : task.id}
        style={chipStyle}
        draggable={!isGhost && (canManage || canReschedule)}
        onDragStart={() => !isGhost && handleDragStart(task)}
        title={
          isGhost
            ? `${t('assignGhostChip')}: ${task.original_start_date}`
            : `${task.title} [${statusLabel(task.status, t)}]${task.elapsed_seconds > 0 ? ' ' + minutesToDisplay(Math.floor(task.elapsed_seconds / 60)) : ''}`
        }
      >
        <span style={{ flex: 1, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
          {label}
        </span>
        {!isGhost && canManage && (
          <>
            <button
              onClick={e => { e.stopPropagation(); setReassignTarget({ taskId: task.id, task }) }}
              title={t('assignReassign')}
              style={{ background: 'none', border: 'none', cursor: 'pointer', fontSize: 10, padding: '0 1px', color: 'var(--text-secondary)' }}
            >↔</button>
            {task.status !== 'done' && (
              <button
                onClick={e => { e.stopPropagation(); doWithdraw(task.id, false) }}
                title={t('assignWithdraw')}
                style={{ background: 'none', border: 'none', cursor: 'pointer', fontSize: 10, padding: '0 1px', color: 'var(--danger)' }}
              >✕</button>
            )}
          </>
        )}
        {!isGhost && canReschedule && (
          <button
            onClick={e => { e.stopPropagation(); setRescheduleTarget({ taskId: task.id, task }) }}
            title={t('assignReschedule')}
            style={{ background: 'none', border: 'none', cursor: 'pointer', fontSize: 10, padding: '0 1px', color: 'var(--text-secondary)' }}
          >📅</button>
        )}
      </div>
    )
  }

  return (
    <div style={{ padding: 16, fontFamily: 'sans-serif', minHeight: '100vh' }}>
      {toast && (
        <div style={{
          position: 'fixed', top: 16, right: 16, zIndex: 9999,
          background: 'var(--bg-elevated)', color: 'var(--text-primary)', padding: '8px 16px',
          borderRadius: 4, fontSize: 14, border: '1px solid var(--border)',
        }}>
          {toast}
        </div>
      )}

      <h2 style={{ marginTop: 0 }}>{t('assignBoardTitle')}</h2>

      {/* Controls */}
      <div style={{ display: 'flex', gap: 10, marginBottom: 12, flexWrap: 'wrap', alignItems: 'center' }}>
        <label style={{ fontSize: 13 }}>
          起始日期：
          <input type="date" value={rangeStart} onChange={e => setRangeStart(e.target.value)} style={{ marginLeft: 4, fontSize: 13 }} />
        </label>
        <label style={{ fontSize: 13 }}>
          天数：
          <select value={rangeDays} onChange={e => setRangeDays(+e.target.value)} style={{ marginLeft: 4, fontSize: 13 }}>
            {[7, 14, 21, 28].map(n => <option key={n} value={n}>{n}</option>)}
          </select>
        </label>
        {!isMember && (
          <label style={{ fontSize: 13 }}>
            成员：
            <select value={filterMemberId} onChange={e => setFilterMemberId(e.target.value)} style={{ marginLeft: 4, fontSize: 13 }}>
              <option value="">全部</option>
              {members.map(m => <option key={m.id} value={m.id}>{m.display_name} ({m.group_name})</option>)}
            </select>
          </label>
        )}
        {isManagerOrAdmin && (
          <>
            <button
              onClick={() => setAssignModal({ mode: 'single' })}
              style={{ padding: '4px 12px', fontSize: 13, cursor: 'pointer', background: 'var(--accent)', color: 'var(--accent-contrast)', border: 'none', borderRadius: 4 }}
            >
              + 分派
            </button>
            <button
              onClick={() => setAssignModal({ mode: 'cartesian' })}
              style={{ padding: '4px 12px', fontSize: 13, cursor: 'pointer', background: 'var(--info)', color: 'var(--accent-contrast)', border: 'none', borderRadius: 4 }}
            >
              ⊗ {t('assignCartesian')}
            </button>
          </>
        )}
        <button onClick={loadAll} style={{ padding: '4px 8px', fontSize: 13, cursor: 'pointer' }}>🔄</button>
      </div>

      {error && (
        <div style={{ color: 'var(--danger)', marginBottom: 8, display: 'flex', alignItems: 'center', gap: 8 }}>
          {error}
          <button
            onClick={loadAll}
            style={{ fontSize: 12, padding: '2px 8px', cursor: 'pointer', border: '1px solid var(--danger)', borderRadius: 3, background: 'none', color: 'var(--danger)' }}
          >重试</button>
        </div>
      )}
      {loading && <div style={{ color: 'var(--text-muted)', marginBottom: 8 }}>{t('assignLoading')}</div>}

      {/* Board — T001: overflow:auto on both axes creates isolated scroll context; sticky members/headers work within it (§3.5) */}
      <div style={{ overflow: 'auto', maxHeight: 'calc(100vh - 200px)' }}>
        <table style={{ borderCollapse: 'collapse', fontSize: 12 }}>
          <thead>
            <tr>
              <th style={{ ...headerCellStyle, minWidth: 110, left: 0, zIndex: 4 }}>成员</th>
              {dates.map(d => {
                const isWeekend = weekdayIndex(d) === 0 || weekdayIndex(d) === 6
                return (
                  <th key={d} style={{ ...headerCellStyle, background: isWeekend ? 'var(--warning-bg)' : 'var(--bg-page)', color: isWeekend ? 'var(--warning)' : 'var(--text-secondary)' }}>
                    {formatDate(d)}
                    <br />
                    <span style={{ fontWeight: 400, fontSize: 10 }}>
                      {['日', '一', '二', '三', '四', '五', '六'][weekdayIndex(d)]}
                    </span>
                  </th>
                )
              })}
            </tr>
          </thead>
          <tbody>
            {visibleMembers.map(member => (
              <tr key={member.id}>
                <td style={memberCellStyle}>
                  <div>{member.display_name}</div>
                  <div style={{ fontSize: 10, color: 'var(--text-muted)' }}>{member.group_name} · {member.region_code}</div>
                </td>
                {dates.map(d => {
                  const cellKey = `${member.id}_${d}`
                  const cellTasks = tasksByCell[cellKey] || []
                  const ghostTasks = ghostByCell[cellKey] || []
                  const conflict = conflictMap[cellKey]

                  return (
                    <td
                      key={d}
                      style={{
                        ...cellStyle,
                        background: conflict?.level === 'yellow' ? 'var(--warning-bg)' : conflict?.level === 'blue' ? 'var(--info-bg)' : undefined,
                      }}
                      onDragOver={e => { if (isManagerOrAdmin || (isMember && member.id === user?.id)) e.preventDefault() }}
                      onDrop={() => handleDrop(member.id, d)}
                    >
                      {renderConflictDot(member.id, d)}
                      {ghostTasks.map(task => renderChip(task, true))}
                      {cellTasks.map(task => renderChip(task, false))}
                      {isManagerOrAdmin && (
                        <button
                          onClick={() => {
                            if (conflict?.level === 'yellow') {
                              if (!window.confirm(t('assignConflictYellow'))) return
                            }
                            setAssignModal({ mode: 'single', prefillMemberId: member.id, prefillDate: d })
                          }}
                          style={{ background: 'none', border: '1px dashed var(--border)', borderRadius: 3, cursor: 'pointer', fontSize: 13, color: 'var(--text-muted)', width: 20, height: 20, padding: 0, marginTop: 1 }}
                          title="分派任务"
                        >+</button>
                      )}
                    </td>
                  )
                })}
              </tr>
            ))}
          </tbody>
        </table>
      </div>

      {visibleMembers.length === 0 && !loading && (
        <div style={{ color: 'var(--text-muted)', marginTop: 16 }}>{t('assignNoTasks')}</div>
      )}

      {/* Modals */}
      {assignModal && (
        <AssignModal
          mode={assignModal.mode}
          prefillMemberId={assignModal.prefillMemberId}
          prefillDate={assignModal.prefillDate}
          members={members}
          taskDict={taskDict}
          conflictMap={conflictMap}
          token={accessToken}
          onSuccess={(newTasks) => {
            setAssignModal(null)
            showToast(t('assignSuccess'))
            // Bug-b fix: optimistically append new chips so board updates immediately
            if (newTasks.length > 0) setTasks(prev => [...prev, ...newTasks])
            loadAll()
          }}
          onCancel={() => setAssignModal(null)}
          t={t}
        />
      )}

      {withdrawNeeds && (
        <ConfirmModal
          title={t('assignWithdraw')}
          message={`${t('assignWithdrawConfirm')} (已用时 ${minutesToDisplay(Math.floor(withdrawNeeds.elapsed_seconds / 60))})`}
          onConfirm={() => doWithdraw(withdrawNeeds.taskId, true)}
          onCancel={() => setWithdrawNeeds(null)}
        />
      )}

      {reassignTarget && (
        <ReassignModal
          task={reassignTarget.task}
          members={members}
          conflictMap={conflictMap}
          onConfirm={(newMid, newDate) => doReassign(reassignTarget.taskId, newMid, newDate)}
          onCancel={() => setReassignTarget(null)}
          t={t}
        />
      )}

      {rescheduleTarget && (
        <RescheduleModal
          task={rescheduleTarget.task}
          onConfirm={newDate => doReschedule(rescheduleTarget.taskId, newDate)}
          onCancel={() => setRescheduleTarget(null)}
          t={t}
        />
      )}
    </div>
  )
}

// ── AssignModal ──────────────────────────────────────────────────────────────

interface AssignModalProps {
  mode: 'single' | 'bundle' | 'cartesian'
  prefillMemberId?: string
  prefillDate?: string
  members: MemberInfo[]
  taskDict: TaskDictItem[]
  conflictMap: Record<string, ConflictResult>
  token: string | null
  onSuccess: (newTasks: AssignedTask[]) => void
  onCancel: () => void
  t: (k: string) => string
}

interface CandidateAssignment {
  assignee_id: string
  start_date: string
  task_id: string | null
  custom_title: string | null
}

interface DupDisplayItem {
  memberName: string
  date: string
  taskName: string
}

function AssignModal({ mode: initialMode, prefillMemberId, prefillDate, members, taskDict, conflictMap, token, onSuccess, onCancel, t }: AssignModalProps) {
  const [mode, setMode] = useState<'single' | 'bundle' | 'cartesian'>(initialMode)
  const [selectedMemberId, setSelectedMemberId] = useState(prefillMemberId || '')
  // T006: single/bundle support multi-date selection
  const [selectedDates, setSelectedDates] = useState<string[]>(prefillDate ? [prefillDate] : [todayStr()])
  const [newSingleDate, setNewSingleDate] = useState('')
  const [singleDateHint, setSingleDateHint] = useState('')
  const [selectedTaskIds, setSelectedTaskIds] = useState<string[]>([])
  const [customTitle, setCustomTitle] = useState('')
  const [cartMemberIds, setCartMemberIds] = useState<string[]>(prefillMemberId ? [prefillMemberId] : [])
  const [cartDates, setCartDates] = useState<string[]>(prefillDate ? [prefillDate] : [])
  const [cartTaskIds, setCartTaskIds] = useState<string[]>([])
  const [newCartDate, setNewCartDate] = useState('')
  const [dateAddHint, setDateAddHint] = useState('')
  const [submitting, setSubmitting] = useState(false)
  const [submitError, setSubmitError] = useState('')
  const [dupConfirm, setDupConfirm] = useState<{
    items: DupDisplayItem[]
    body: Record<string, unknown>
  } | null>(null)
  const [checking, setChecking] = useState(false)

  const cartTotal = cartMemberIds.length * cartDates.length * cartTaskIds.length

  // Show conflict indicator for single mode when exactly one date is selected
  const conflictLevel = useMemo(() => {
    if (mode !== 'single' || !selectedMemberId || selectedDates.length !== 1) return 'none'
    return conflictMap[`${selectedMemberId}_${selectedDates[0]}`]?.level || 'none'
  }, [mode, selectedMemberId, selectedDates, conflictMap])

  function candidateDupKey(c: CandidateAssignment): string {
    return `${c.assignee_id}__${c.start_date}__${c.task_id || `|${c.custom_title || ''}`}`
  }

  function existingAssignDupKey(a: AssignedTask): string {
    return `${a.assignee_id}__${a.start_date}__${a.task_id || `${a.category_code || ''}|${a.title || ''}`}`
  }

  async function findDuplicates(candidates: CandidateAssignment[]): Promise<DupDisplayItem[]> {
    if (candidates.length === 0) return []
    const allDates = candidates.map(c => c.start_date)
    const dateFrom = allDates.reduce((a, b) => a < b ? a : b)
    const dateTo = allDates.reduce((a, b) => a > b ? a : b)

    let existing: AssignedTask[] = []
    try {
      const res = await fetch(`/api/assignments?date_from=${dateFrom}&date_to=${dateTo}`, {
        headers: authHeader(token),
      })
      if (res.ok) {
        const data = await res.json()
        existing = data.data || []
      }
    } catch {
      return [] // network error → skip dup check, proceed to submit
    }

    const existingKeys = new Set(existing.map(a => existingAssignDupKey(a)))
    const candidateKeyCounts = new Map<string, number>()
    for (const c of candidates) {
      const key = candidateDupKey(c)
      candidateKeyCounts.set(key, (candidateKeyCounts.get(key) || 0) + 1)
    }

    const seen = new Set<string>()
    const dups: DupDisplayItem[] = []
    for (const c of candidates) {
      const key = candidateDupKey(c)
      if (seen.has(key)) continue
      if (existingKeys.has(key) || (candidateKeyCounts.get(key) || 0) >= 2) {
        seen.add(key)
        const member = members.find(m => m.id === c.assignee_id)
        const task = c.task_id ? taskDict.find(td => td.id === c.task_id) : null
        dups.push({
          memberName: member?.display_name || c.assignee_id,
          date: c.start_date,
          taskName: task?.name || c.custom_title || '未知任务',
        })
      }
    }
    return dups
  }

  async function doSubmit(body: Record<string, unknown>) {
    setSubmitting(true)
    let successTasks: AssignedTask[] | null = null
    try {
      const res = await fetch('/api/assignments', {
        method: 'POST',
        headers: { ...authHeader(token), 'Content-Type': 'application/json' },
        body: JSON.stringify(body),
      })
      const data = await res.json()
      if (!res.ok) { setSubmitError(data.error?.message || '分派失败'); return }
      successTasks = data.tasks || []
    } catch (err) {
      setSubmitError(err instanceof TypeError ? '网络错误' : '请求失败，请重试')
    } finally {
      setSubmitting(false)
    }
    if (successTasks !== null) onSuccess(successTasks)
  }

  async function handleSubmit() {
    setSubmitError('')
    if (conflictLevel === 'yellow' && !window.confirm(t('assignConflictYellow'))) return

    let body: Record<string, unknown>
    let candidates: CandidateAssignment[]

    if (mode === 'cartesian') {
      if (!cartMemberIds.length) { setSubmitError('请至少选择一个成员'); return }
      if (!cartDates.length) { setSubmitError('请至少选择一个日期（选日期后点「添加」）'); return }
      if (!cartTaskIds.length) { setSubmitError('请至少选择一个任务'); return }
      body = { member_ids: cartMemberIds, dates: cartDates, task_ids: cartTaskIds }
      candidates = cartMemberIds.flatMap(mid =>
        cartDates.flatMap(d =>
          cartTaskIds.map(tid => ({ assignee_id: mid, start_date: d, task_id: tid, custom_title: null }))
        )
      )
    } else if (mode === 'bundle') {
      if (!selectedMemberId) { setSubmitError('请选择成员'); return }
      if (!selectedDates.length) { setSubmitError('请至少选择一个日期（选日期后点「添加」）'); return }
      if (!selectedTaskIds.length) { setSubmitError('请至少选择一个任务'); return }
      body = {
        assignments: selectedDates.flatMap(d =>
          selectedTaskIds.map(tid => ({ member_id: selectedMemberId, start_date: d, task_id: tid }))
        ),
      }
      candidates = selectedDates.flatMap(d =>
        selectedTaskIds.map(tid => ({ assignee_id: selectedMemberId, start_date: d, task_id: tid, custom_title: null }))
      )
    } else {
      // single
      if (!selectedMemberId) { setSubmitError('请选择成员'); return }
      if (!selectedDates.length) { setSubmitError('请至少选择一个日期（选日期后点「添加」）'); return }
      if (!selectedTaskIds[0] && !customTitle) { setSubmitError('请选择任务或输入自定义标题'); return }
      body = {
        assignments: selectedDates.map(d => {
          const item: Record<string, string> = { member_id: selectedMemberId, start_date: d }
          if (selectedTaskIds[0]) item.task_id = selectedTaskIds[0]
          else item.title = customTitle
          return item
        }),
      }
      candidates = selectedDates.map(d => ({
        assignee_id: selectedMemberId,
        start_date: d,
        task_id: selectedTaskIds[0] || null,
        custom_title: selectedTaskIds[0] ? null : customTitle,
      }))
    }

    setChecking(true)
    let dups: DupDisplayItem[] = []
    try {
      dups = await findDuplicates(candidates)
    } finally {
      setChecking(false)
    }

    if (dups.length > 0) {
      setDupConfirm({ items: dups, body })
      return
    }

    await doSubmit(body)
  }

  const overlayStyle: React.CSSProperties = { position: 'fixed', inset: 0, background: 'var(--overlay)', zIndex: 1000, display: 'flex', alignItems: 'center', justifyContent: 'center' }
  const modalStyle: React.CSSProperties = { background: 'var(--bg-surface)', color: 'var(--text-primary)', borderRadius: 8, padding: 24, minWidth: 380, maxWidth: 560, maxHeight: '90vh', overflowY: 'auto', boxShadow: '0 4px 24px rgba(0,0,0,0.2)' }

  return (
    <div style={overlayStyle} onClick={e => e.target === e.currentTarget && onCancel()}>
      <div style={modalStyle}>
        <h3 style={{ marginTop: 0 }}>分派任务</h3>
        <div style={{ marginBottom: 12, display: 'flex', gap: 8 }}>
          {(['single', 'bundle', 'cartesian'] as const).map(m => (
            <button key={m} onClick={() => setMode(m)} style={{ padding: '3px 10px', fontSize: 12, cursor: 'pointer', borderRadius: 4, background: mode === m ? 'var(--accent)' : 'var(--bg-elevated)', color: mode === m ? 'var(--accent-contrast)' : 'var(--text-primary)', border: '1px solid var(--border)' }}>
              {m === 'single' ? '单任务' : m === 'bundle' ? 'Bundle' : t('assignCartesian')}
            </button>
          ))}
        </div>

        {(mode === 'single' || mode === 'bundle') && (
          <>
            <label style={labelStyle}>
              成员
              <select value={selectedMemberId} onChange={e => setSelectedMemberId(e.target.value)} style={inputStyle}>
                <option value="">请选择</option>
                {members.map(m => <option key={m.id} value={m.id}>{m.display_name} ({m.group_name})</option>)}
              </select>
            </label>
            <div style={{ fontSize: 12, fontWeight: 600, marginBottom: 4 }}>{t('assignSelectDates')}</div>
            <div style={{ display: 'flex', gap: 6, marginBottom: 4, alignItems: 'center', flexWrap: 'wrap' }}>
              <input
                type="date"
                value={newSingleDate}
                onChange={e => { setNewSingleDate(e.target.value); setSingleDateHint('') }}
                style={{ fontSize: 12, padding: 2 }}
              />
              <button
                onClick={() => {
                  if (!newSingleDate) { setSingleDateHint('请先选择日期'); return }
                  if (selectedDates.includes(newSingleDate)) { setSingleDateHint('该日期已添加'); return }
                  setSelectedDates(prev => [...prev, newSingleDate].sort())
                  setNewSingleDate('')
                  setSingleDateHint('')
                }}
                style={{ fontSize: 12, padding: '2px 8px', cursor: 'pointer' }}
              >
                添加
              </button>
              {singleDateHint && <span style={{ color: 'var(--warning)', fontSize: 11 }}>{singleDateHint}</span>}
            </div>
            <div style={{ marginBottom: 10, fontSize: 12, display: 'flex', flexWrap: 'wrap', gap: 4 }}>
              {selectedDates.map(d => (
                <span key={d} style={{ display: 'inline-flex', alignItems: 'center', background: 'var(--info-bg)', borderRadius: 4, padding: '2px 6px', gap: 4, color: 'var(--info)' }}>
                  {d}
                  <button onClick={() => setSelectedDates(prev => prev.filter(x => x !== d))} style={{ background: 'none', border: 'none', cursor: 'pointer', fontSize: 11, padding: 0, color: 'var(--info)' }}>✕</button>
                </span>
              ))}
              {!selectedDates.length && <span style={{ color: 'var(--text-muted)' }}>暂未选择日期</span>}
            </div>
            {conflictLevel === 'yellow' && <div style={{ color: 'var(--warning)', fontSize: 12, marginBottom: 8 }}>⚠️ {t('assignConflictYellow')}</div>}
            {conflictLevel === 'blue' && <div style={{ color: 'var(--info)', fontSize: 12, marginBottom: 8 }}>ℹ️ {t('assignConflictBlue')}</div>}
            <div style={{ fontSize: 12, marginBottom: 4 }}>任务（{mode === 'bundle' ? '可多选' : '单选'}）</div>
            <div style={{ maxHeight: 180, overflowY: 'auto', border: '1px solid var(--border)', borderRadius: 4, marginBottom: 10 }}>
              {taskDict.length === 0
                ? <div style={{ padding: '12px 10px', color: 'var(--text-muted)', fontSize: 12, textAlign: 'center' }}>暂无可分派的任务</div>
                : taskDict.map(task => {
                  const checked = selectedTaskIds.includes(task.id)
                  return (
                    <label key={task.id} style={{ display: 'flex', alignItems: 'center', gap: 6, padding: '3px 8px', cursor: 'pointer', background: checked ? 'var(--info-bg)' : undefined }}>
                      <input
                        type={mode === 'bundle' ? 'checkbox' : 'radio'}
                        name="task_sel"
                        checked={checked}
                        onChange={() => {
                          if (mode === 'bundle') setSelectedTaskIds(prev => checked ? prev.filter(id => id !== task.id) : [...prev, task.id])
                          else { setSelectedTaskIds([task.id]); setCustomTitle('') }
                        }}
                      />
                      <span style={{ fontSize: 12 }}>{task.name}</span>
                    </label>
                  )
                })}
            </div>
            {mode === 'single' && (
              <label style={labelStyle}>
                自定义标题（无字典项时使用）
                <input type="text" value={customTitle} onChange={e => { setCustomTitle(e.target.value); setSelectedTaskIds([]) }} placeholder="留空则需选上方任务" style={inputStyle} />
              </label>
            )}
          </>
        )}

        {mode === 'cartesian' && (
          <>
            <div style={{ fontSize: 12, fontWeight: 600, marginBottom: 4 }}>{t('assignSelectMembers')}</div>
            <div style={{ maxHeight: 120, overflowY: 'auto', border: '1px solid var(--border)', borderRadius: 4, marginBottom: 10 }}>
              {members.map(m => {
                const checked = cartMemberIds.includes(m.id)
                return (
                  <label key={m.id} style={{ display: 'flex', alignItems: 'center', gap: 6, padding: '3px 8px', cursor: 'pointer', background: checked ? 'var(--success-bg)' : undefined }}>
                    <input type="checkbox" checked={checked} onChange={() => setCartMemberIds(prev => checked ? prev.filter(id => id !== m.id) : [...prev, m.id])} />
                    <span style={{ fontSize: 12 }}>{m.display_name} ({m.group_name})</span>
                  </label>
                )
              })}
            </div>
            <div style={{ fontSize: 12, fontWeight: 600, marginBottom: 4 }}>{t('assignSelectDates')}</div>
            <div style={{ display: 'flex', gap: 6, marginBottom: 4, alignItems: 'center', flexWrap: 'wrap' }}>
              <input
                type="date"
                value={newCartDate}
                onChange={e => { setNewCartDate(e.target.value); setDateAddHint('') }}
                style={{ fontSize: 12, padding: 2 }}
              />
              <button
                onClick={() => {
                  if (!newCartDate) { setDateAddHint('请先选择日期'); return }
                  if (cartDates.includes(newCartDate)) { setDateAddHint('该日期已添加'); return }
                  setCartDates(prev => [...prev, newCartDate].sort())
                  setNewCartDate('')
                  setDateAddHint('')
                }}
                style={{ fontSize: 12, padding: '2px 8px', cursor: 'pointer' }}
              >
                添加
              </button>
              {dateAddHint && <span style={{ color: 'var(--warning)', fontSize: 11 }}>{dateAddHint}</span>}
            </div>
            <div style={{ marginBottom: 10, fontSize: 12, display: 'flex', flexWrap: 'wrap', gap: 4 }}>
              {cartDates.map(d => (
                <span key={d} style={{ display: 'inline-flex', alignItems: 'center', background: 'var(--info-bg)', borderRadius: 4, padding: '2px 6px', gap: 4, color: 'var(--info)' }}>
                  {d}
                  <button onClick={() => setCartDates(prev => prev.filter(x => x !== d))} style={{ background: 'none', border: 'none', cursor: 'pointer', fontSize: 11, padding: 0, color: 'var(--info)' }}>✕</button>
                </span>
              ))}
              {!cartDates.length && <span style={{ color: 'var(--text-muted)' }}>暂未选择日期</span>}
            </div>
            <div style={{ fontSize: 12, fontWeight: 600, marginBottom: 4 }}>{t('assignSelectTasks')}</div>
            <div style={{ maxHeight: 120, overflowY: 'auto', border: '1px solid var(--border)', borderRadius: 4, marginBottom: 10 }}>
              {taskDict.length === 0
                ? <div style={{ padding: '12px 10px', color: 'var(--text-muted)', fontSize: 12, textAlign: 'center' }}>暂无可分派的任务</div>
                : taskDict.map(task => {
                  const checked = cartTaskIds.includes(task.id)
                  return (
                    <label key={task.id} style={{ display: 'flex', alignItems: 'center', gap: 6, padding: '3px 8px', cursor: 'pointer', background: checked ? 'var(--info-bg)' : undefined }}>
                      <input type="checkbox" checked={checked} onChange={() => setCartTaskIds(prev => checked ? prev.filter(id => id !== task.id) : [...prev, task.id])} />
                      <span style={{ fontSize: 12 }}>{task.name}</span>
                    </label>
                  )
                })}
            </div>
            {cartTotal > 0 && (
              <div style={{ fontSize: 13, color: 'var(--accent)', marginBottom: 8, fontWeight: 600 }}>
                {t('assignCartesianSummary')
                  .replace('{m}', String(cartMemberIds.length))
                  .replace('{d}', String(cartDates.length))
                  .replace('{t}', String(cartTaskIds.length))
                  .replace('{n}', String(cartTotal))}
              </div>
            )}
          </>
        )}

        {dupConfirm ? (
          <>
            <p style={{ fontSize: 14, marginBottom: 8 }}>
              以下 <strong>{dupConfirm.items.length}</strong> 条分派与已存在记录或本批次内重复，是否仍要提交？
            </p>
            <ul style={{ fontSize: 13, marginBottom: 16, paddingLeft: 20, maxHeight: 200, overflowY: 'auto' }}>
              {dupConfirm.items.map((item, i) => (
                <li key={i} style={{ marginBottom: 4, color: 'var(--warning)' }}>
                  {item.memberName} · {item.date} · {item.taskName}
                </li>
              ))}
            </ul>
            {submitError && <div style={{ color: 'var(--danger)', fontSize: 12, marginBottom: 8 }}>{submitError}</div>}
            <div style={{ display: 'flex', gap: 8, justifyContent: 'flex-end' }}>
              <button onClick={() => setDupConfirm(null)} style={{ padding: '6px 14px', cursor: 'pointer', background: 'var(--bg-elevated)', border: '1px solid var(--border)', color: 'var(--text-primary)', borderRadius: 4 }}>取消</button>
              <button
                onClick={() => { const b = dupConfirm.body; setDupConfirm(null); doSubmit(b) }}
                disabled={submitting}
                style={{ padding: '6px 14px', background: 'var(--accent)', color: 'var(--accent-contrast)', border: 'none', borderRadius: 4, cursor: 'pointer' }}
              >
                {submitting ? '提交中...' : '继续'}
              </button>
            </div>
          </>
        ) : (
          <>
            {submitError && <div style={{ color: 'var(--danger)', fontSize: 12, marginBottom: 8 }}>{submitError}</div>}
            <div style={{ display: 'flex', gap: 8, justifyContent: 'flex-end' }}>
              <button onClick={onCancel} style={{ padding: '6px 14px', cursor: 'pointer', background: 'var(--bg-elevated)', border: '1px solid var(--border)', color: 'var(--text-primary)', borderRadius: 4 }}>{t('assignCancel')}</button>
              <button onClick={handleSubmit} disabled={submitting || checking} style={{ padding: '6px 14px', background: 'var(--accent)', color: 'var(--accent-contrast)', border: 'none', borderRadius: 4, cursor: 'pointer' }}>
                {checking ? '检查中...' : submitting ? '提交中...' : t('assignConfirm')}
              </button>
            </div>
          </>
        )}
      </div>
    </div>
  )
}

// ── ReassignModal ────────────────────────────────────────────────────────────

function ReassignModal({ task, members, conflictMap, onConfirm, onCancel, t }: {
  task: AssignedTask
  members: MemberInfo[]
  conflictMap: Record<string, ConflictResult>
  onConfirm: (newMemberId: string, newDate: string) => void
  onCancel: () => void
  t: (k: string) => string
}) {
  const [newMemberId, setNewMemberId] = useState(task.assignee_id)
  const [newDate, setNewDate] = useState(task.start_date)
  const conflict = conflictMap[`${newMemberId}_${newDate}`]

  const handleConfirm = () => {
    if (conflict?.level === 'yellow' && !window.confirm(t('assignConflictYellow'))) return
    onConfirm(newMemberId, newDate)
  }

  const overlayStyle: React.CSSProperties = { position: 'fixed', inset: 0, background: 'var(--overlay)', zIndex: 1000, display: 'flex', alignItems: 'center', justifyContent: 'center' }

  return (
    <div style={overlayStyle} onClick={e => e.target === e.currentTarget && onCancel()}>
      <div style={{ background: 'var(--bg-surface)', color: 'var(--text-primary)', borderRadius: 8, padding: 24, minWidth: 320, boxShadow: '0 4px 24px rgba(0,0,0,0.2)' }}>
        <h3 style={{ marginTop: 0 }}>{t('assignReassignTitle')}: {task.title}</h3>
        <label style={labelStyle}>
          {t('assignNewAssignee')}
          <select value={newMemberId} onChange={e => setNewMemberId(e.target.value)} style={inputStyle}>
            {members.map(m => <option key={m.id} value={m.id}>{m.display_name} ({m.group_name})</option>)}
          </select>
        </label>
        <label style={labelStyle}>
          {t('assignNewDate')}
          <input type="date" value={newDate} onChange={e => setNewDate(e.target.value)} style={inputStyle} />
        </label>
        {conflict?.level === 'yellow' && <div style={{ color: 'var(--warning)', fontSize: 12, marginBottom: 8 }}>⚠️ {t('assignConflictYellow')}</div>}
        {conflict?.level === 'blue' && <div style={{ color: 'var(--info)', fontSize: 12, marginBottom: 8 }}>ℹ️ {t('assignConflictBlue')}</div>}
        <div style={{ display: 'flex', gap: 8, justifyContent: 'flex-end' }}>
          <button onClick={onCancel} style={{ padding: '6px 14px', cursor: 'pointer', background: 'var(--bg-elevated)', border: '1px solid var(--border)', color: 'var(--text-primary)', borderRadius: 4 }}>{t('assignCancel')}</button>
          <button onClick={handleConfirm} style={{ padding: '6px 14px', background: 'var(--accent)', color: 'var(--accent-contrast)', border: 'none', borderRadius: 4, cursor: 'pointer' }}>{t('assignReassign')}</button>
        </div>
      </div>
    </div>
  )
}

// ── RescheduleModal ──────────────────────────────────────────────────────────

function RescheduleModal({ task, onConfirm, onCancel, t }: {
  task: AssignedTask
  onConfirm: (newDate: string) => void
  onCancel: () => void
  t: (k: string) => string
}) {
  const [newDate, setNewDate] = useState(task.start_date)
  const overlayStyle: React.CSSProperties = { position: 'fixed', inset: 0, background: 'var(--overlay)', zIndex: 1000, display: 'flex', alignItems: 'center', justifyContent: 'center' }

  return (
    <div style={overlayStyle} onClick={e => e.target === e.currentTarget && onCancel()}>
      <div style={{ background: 'var(--bg-surface)', color: 'var(--text-primary)', borderRadius: 8, padding: 24, minWidth: 300, boxShadow: '0 4px 24px rgba(0,0,0,0.2)' }}>
        <h3 style={{ marginTop: 0 }}>{t('assignRescheduleTitle')}: {task.title}</h3>
        <div style={{ fontSize: 12, color: 'var(--text-muted)', marginBottom: 8 }}>原计划：{task.original_start_date} → 当前：{task.start_date}</div>
        <label style={labelStyle}>
          新日期
          <input type="date" value={newDate} onChange={e => setNewDate(e.target.value)} style={inputStyle} />
        </label>
        <div style={{ display: 'flex', gap: 8, justifyContent: 'flex-end' }}>
          <button onClick={onCancel} style={{ padding: '6px 14px', cursor: 'pointer', background: 'var(--bg-elevated)', border: '1px solid var(--border)', color: 'var(--text-primary)', borderRadius: 4 }}>{t('assignCancel')}</button>
          <button onClick={() => onConfirm(newDate)} style={{ padding: '6px 14px', background: 'var(--accent)', color: 'var(--accent-contrast)', border: 'none', borderRadius: 4, cursor: 'pointer' }}>{t('assignReschedule')}</button>
        </div>
      </div>
    </div>
  )
}

// ── ConfirmModal ─────────────────────────────────────────────────────────────

function ConfirmModal({ title, message, onConfirm, onCancel }: {
  title: string
  message: string
  onConfirm: () => void
  onCancel: () => void
}) {
  const overlayStyle: React.CSSProperties = { position: 'fixed', inset: 0, background: 'var(--overlay)', zIndex: 1000, display: 'flex', alignItems: 'center', justifyContent: 'center' }

  return (
    <div style={overlayStyle} onClick={e => e.target === e.currentTarget && onCancel()}>
      <div style={{ background: 'var(--bg-surface)', color: 'var(--text-primary)', borderRadius: 8, padding: 24, minWidth: 300, boxShadow: '0 4px 24px rgba(0,0,0,0.2)' }}>
        <h3 style={{ marginTop: 0 }}>{title}</h3>
        <p style={{ fontSize: 14 }}>{message}</p>
        <div style={{ display: 'flex', gap: 8, justifyContent: 'flex-end' }}>
          <button onClick={onCancel} style={{ padding: '6px 14px', cursor: 'pointer', background: 'var(--bg-elevated)', border: '1px solid var(--border)', color: 'var(--text-primary)', borderRadius: 4 }}>取消</button>
          <button onClick={onConfirm} style={{ padding: '6px 14px', background: 'var(--danger)', color: 'var(--accent-contrast)', border: 'none', borderRadius: 4, cursor: 'pointer' }}>确认</button>
        </div>
      </div>
    </div>
  )
}
